import { Box, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { Gauge, gaugeClasses } from '@mui/x-charts/Gauge';
import getDocumentScore from '@/components/Quartermaster/Inspections/getDocumentScore';
import getScoreCategoryDetails from '../../getScoreCategoryDetails';

interface InspectionResultGaugeProps {
  critical_non_compliant: number;
  non_critical_non_compliant: number;
  critical_compliant: number;
  non_critical_compliant: number;
}

const getCaption = (
  score_category: ReturnType<typeof getScoreCategoryDetails>,
  critical_check_count: number,
  regular_check_count: number,
  critical_check_passed: number,
  regular_check_passed: number,
): string => {
  switch (score_category.value) {
    case 'critical':
      if (critical_check_count > 0) {
        return `Failed ${
          critical_check_count - critical_check_passed
        } of ${critical_check_count} critical requirements`;
      } else {
        return `Failed ${
          regular_check_count - regular_check_passed
        } of ${regular_check_count} requirements`;
      }
    case 'growth':
      return `Passed all critical requirements but failed ${
        regular_check_count - regular_check_passed
      } of ${regular_check_count} non-critical requirements`;
    case 'golden':
      if (regular_check_count === 0) {
        return 'Passed all critical requirements';
      }
      return `Passed all critical requirements and ${regular_check_passed} of ${regular_check_count} non-critical requirements`;
    default:
      return ``;
  }
};

function InspectionResultGauge({
  critical_non_compliant,
  non_critical_non_compliant,
  critical_compliant,
  non_critical_compliant,
}: InspectionResultGaugeProps): React.JSX.Element {
  const critical_check_count = critical_non_compliant + critical_compliant;
  const regular_check_count =
    non_critical_non_compliant + non_critical_compliant;

  const value = getDocumentScore(
    critical_check_count,
    regular_check_count,
    critical_compliant,
    non_critical_compliant,
  );
  const score_category = getScoreCategoryDetails(value);

  const caption = getCaption(
    score_category,
    critical_check_count,
    regular_check_count,
    critical_compliant,
    non_critical_compliant,
  );

  return (
    <Box padding={'10px'}>
      <Typography variant='subtitle1'>Document Health Status</Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 4, lg: 4 }}>
          <Gauge
            width={250}
            height={250}
            value={value}
            startAngle={-110}
            endAngle={110}
            sx={{
              [`& .${gaugeClasses.valueText}`]: {
                fontSize: 40,
                transform: 'translate(0px, 0px)',
              },
              [`& .${gaugeClasses.valueArc}`]: {
                fill: score_category.color,
              },
            }}
            text={({ value }) => `${value}`}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6, lg: 6 }} sx={{ alignContent: 'center' }}>
          <Typography variant='subtitle1'>
            Rating: <b>{score_category.label}</b>
          </Typography>
          <br />
          <Typography variant='caption'>
            {score_category.description}
          </Typography>
          <br />
          <br />
          <Typography variant='body2'>{caption}</Typography>
        </Grid>
      </Grid>
    </Box>
  );
}

export default InspectionResultGauge;
